import React from 'react';
import { useSearchParams } from 'react-router-dom';
import './PolicyPage.css';

// 새 환불정책 시행일 (개인정보처리방침/이용약관과 동일)
// 2025-12-18 부 시행 후, 2026-04-25 부 정책 단순화 (사용량 차감 → 7일 이내 자동 환불 / 7일 초과 문의)
const NEW_POLICY_EFFECTIVE_DATE = new Date('2025-12-18');
const SIMPLIFIED_POLICY_REVISION_DATE = '2026-04-25';

const RefundPolicy = () => {
  const [searchParams] = useSearchParams();
  const now = new Date();
  
  const isPreviewMode = searchParams.get('preview') === 'new';
  const isNewPolicyEffective = isPreviewMode || now >= NEW_POLICY_EFFECTIVE_DATE;

  return isNewPolicyEffective ? <NewRefundPolicy isPreview={isPreviewMode} /> : <CurrentRefundPolicy />;
};

// ============================================================
// 새 환불정책 (2025-12-18 이후 적용)
// AI 서비스 비용 구조 반영 - 사용량 기반 차감 환불
// ============================================================
const NewRefundPolicy = ({ isPreview = false }) => {
  return (
    <div className="policy-page">
      <main className="policy-document">
        <header className="policy-header">
          <h1 className="policy-title">
            환불 정책
          </h1>
        </header>

        {isPreview && (
          <p className="policy-preview">
            미리보기 모드 - 이 정책은 2025년 12월 18일부터 정식 적용됩니다
          </p>
        )}

        <div className="policy-effective">
          <strong>
            시행일: 2025년 12월 18일 | 최종 개정일: {SIMPLIFIED_POLICY_REVISION_DATE.replace(/-/g, '. ')}
          </strong>
          <span>전자상거래 등에서의 소비자보호에 관한 법률 준수</span>
        </div>

        <PolicySection title="제1조 (환불 정책의 목적 및 법적 근거)">
          <p>① 이 환불 정책은 플랙티컬 주식회사가 제공하는 왓퀴즈 Pro 구독 서비스의 환불 기준과 절차를 명확히 합니다.</p>
          <p>② 본 정책은 「전자상거래 등에서의 소비자보호에 관한 법률」 제17조 및 「콘텐츠이용자보호지침」에 따라 제정되었습니다.</p>
          <p>③ <strong>AI 서비스의 특수성:</strong> 생성형 AI 서비스는 사용자의 요청마다 실시간으로 API 비용이 발생하는 변동비 구조입니다. 본 정책은 회원의 청약철회권을 보장하는 동시에 이러한 비용 구조를 합리적으로 반영합니다.</p>
        </PolicySection>

        <PolicySection title="제2조 (환불 기준)">
          <div className="policy-card policy-card--success">
            <p><strong>결제 후 7일 이내: 100% 전액 환불</strong></p>
            <ul>
              <li>결제일로부터 <strong>7일 이내</strong>에 마이페이지 → 구독 관리 → 환불 요청 시 자동으로 처리됩니다.</li>
              <li><strong>결제 금액 전액</strong>이 환불됩니다.</li>
              <li>환불 신청 즉시 PRO 혜택이 종료됩니다.</li>
            </ul>
          </div>

          <div className="policy-card policy-card--warning">
            <p><strong>결제 후 7일 경과: 자동 환불 불가, 별도 문의</strong></p>
            <ul>
              <li>결제 후 7일이 지난 시점부터는 마이페이지에서 자동 환불이 불가합니다.</li>
              <li>AI 서비스는 사용 시 즉시 API 비용이 발생하는 디지털 콘텐츠로, 「전자상거래법」 제17조 제2항 제5호에 따라 청약철회가 제한될 수 있습니다.</li>
              <li>회사 귀책사유, 서비스 장애, 명백한 오류 등 특별한 사정이 있는 경우 <strong>angel@plactical.com</strong> 으로 문의 주시면 사안별로 검토하여 안내드립니다.</li>
            </ul>
          </div>

          <div className="policy-card policy-card--info">
            <p><strong>미성년자 결제에 관한 특례</strong></p>
            <ul>
              <li>만 19세 미만 미성년자가 법정대리인의 동의 없이 결제한 경우, 「민법」 제5조 및 「전자상거래 등에서의 소비자보호에 관한 법률」 제17조 제1항 제1호에 따라 <strong>결제 후 경과 기간과 무관하게</strong> 청약철회(취소) 및 전액 환불이 가능합니다.</li>
              <li>본 조항에 따른 환불 신청은 <a href="mailto:angel@plactical.com">angel@plactical.com</a>으로 결제일·결제수단·법정대리인 신원 확인 자료를 첨부하여 접수해 주시기 바랍니다.</li>
            </ul>
          </div>

          <p className="policy-muted">
            ※ 본 조항은 회원이 청약철회권을 행사할 수 있는 「전자상거래 등에서의 소비자보호에 관한 법률」 제17조 제1항(7일 이내 청약철회)을 충실히 보장하기 위한 것입니다.
          </p>
        </PolicySection>

        <PolicySection title="제3조 (환불 제외 사항)">
          <div className="policy-card policy-card--danger">
            <p><strong>다음의 경우 환불 대상에서 제외됩니다</strong></p>
            <ul>
              <li><strong>쿠폰·추천인·이벤트 혜택:</strong> 쿠폰 등록, 추천인 코드, 무료 이벤트 등으로 받은 PRO 이용권 (실제 결제 이력이 없는 경우)</li>
              <li><strong>학교·기관 단체 구매(대량구매) 중 이미 등록된 쿠폰:</strong> 단체 구매 환불은 발급된 쿠폰 중 사용되지 않은 분에 대해서만 가능합니다.</li>
              <li><strong>이용약관 위반으로 인한 서비스 중단:</strong> 약관 위반에 따라 회사가 적법하게 이용을 제한한 경우</li>
              <li><strong>허위 정보 제공으로 인한 계약 해지</strong></li>
            </ul>
          </div>
        </PolicySection>

        <PolicySection title="제4조 (AI 서비스 특성에 따른 면책)">
          <div className="policy-card policy-card--danger">
            <p><strong>환불 사유로 인정되지 않는 경우</strong></p>
            <ul>
              <li><strong>AI 답변의 부정확성:</strong> AI는 확률적 모델로 작동하며, 환각 현상(Hallucination)이 발생할 수 있습니다. 이는 서비스의 하자가 아닙니다.</li>
              <li><strong>기대 수준 미달:</strong> AI 결과물이 회원의 주관적 기대에 미치지 못하는 경우</li>
              <li><strong>사용 후 단순 변심:</strong> AI 서비스를 이미 사용한 후의 단순 변심</li>
            </ul>
            <p>
              ※ 단, 서버 장애 등 회사 귀책사유로 인한 서비스 불가 시에는 전액 환불합니다.
            </p>
          </div>
        </PolicySection>

        <PolicySection title="제5조 (오남용 방지 및 환불 제한)">
          <p>공정한 서비스 이용 환경을 위해 다음의 경우 환불이 거부될 수 있습니다:</p>
          <div className="policy-card policy-card--warning">
            <ul>
              <li><strong>자동화된 대량 사용:</strong> 매크로, 봇, 스크립트 등을 이용한 자동화된 데이터 수집 및 생성</li>
              <li><strong>비정상적 과다 사용:</strong> 24시간 내 일 평균 대비 300% 이상 사용 후 환불 요청</li>
              <li><strong>단기 집중 이용 후 환불:</strong> 결제 후 단기간 내 통상적 이용량을 현저히 초과하는 사용을 한 뒤 환불을 요청하여, 「전자상거래법」 제17조 제2항 제5호의 청약철회 제한 사유에 해당한다고 객관적으로 인정되는 경우</li>
              <li><strong>계정 공유:</strong> 타인과 계정을 공유하거나 양도하는 행위</li>
              <li><strong>반복적 환불:</strong> 12개월 내 2회 이상 환불 이력이 있는 경우</li>
              <li><strong>이용약관 위반:</strong> 위법 콘텐츠 생성, 역공학 시도 등</li>
            </ul>
            <p>
              ※ 위 사유에 해당하는 경우 회사는 환불을 거부하거나 사용량 비례 차감 후 잔액만 환불할 수 있으며, 자동 환불 처리 후 사후 적발 시에는 손해배상을 청구할 수 있습니다.
            </p>
          </div>
        </PolicySection>

        <PolicySection title="제6조 (환불 신청 방법)">
          <ol>
            <li>
              <strong>결제 후 7일 이내 — 마이페이지에서 자동 환불</strong>
              <p className="policy-muted">
                마이페이지 → 구독 관리 → [환불 요청] 버튼 클릭 → 100% 전액 자동 환불
              </p>
            </li>
            <li>
              <strong>결제 후 7일 경과 — 이메일 문의</strong>
              <p className="policy-muted">
                자동 환불은 불가하며, 아래 연락처로 문의 부탁드립니다.<br/>
                이메일: <a href="mailto:angel@plactical.com">angel@plactical.com</a> / 전화: 02-949-9158
              </p>
            </li>
            <li>
              <strong>환불 처리</strong>
              <p className="policy-muted">
                자동 환불은 신청 즉시 PRO 혜택이 종료되며, 환불 금액은 영업일 기준 3-5일 내 결제 카드/계좌로 환급됩니다.
              </p>
            </li>
          </ol>
        </PolicySection>

        <PolicySection title="제7조 (소비자 분쟁 해결)">
          <div className="policy-card policy-card--info">
            <ul>
              <li>한국소비자원 소비자분쟁조정위원회: 1372</li>
              <li>온라인 신청: www.ccn.go.kr</li>
            </ul>
          </div>
        </PolicySection>

        <PolicySection title="부칙">
          <p>① 이 정책은 2025년 12월 18일부터 시행되며, {SIMPLIFIED_POLICY_REVISION_DATE.replace(/-/g, '. ')} 부 단순화 개정되어 즉시 적용됩니다.</p>

          <div className="policy-card policy-card--info">
            <p>
              ② 주요 변경사항 ({SIMPLIFIED_POLICY_REVISION_DATE.replace(/-/g, '. ')} 개정):<br/>
              • <strong>환불 기준 단순화</strong> - 7일 이내 100% 자동 환불 / 7일 초과 별도 문의로 통일<br/>
              • <strong>사용량 기반 차감 산정 폐지</strong> - 사용량·기간 비례 차감 공식 제거<br/>
              • <strong>AI 생성 건당 단가표 폐지</strong> - 환불 산정에서 사용량 단가를 사용하지 않음<br/>
              • <strong>위약금 10% 폐지</strong> - 7일 이내 환불 시 위약금 부과 없음<br/>
              • <strong>미성년자 결제 특례 신설 (제2조)</strong> - 법정대리인 동의 없는 결제는 기간 무관 청약철회 가능 (「민법」 제5조)<br/>
              • <strong>30일 유예 조항 신설 (부칙 ④)</strong> - 시행 전 결제 회원의 직전 정책 환불 신청권 보장 (「약관규제법」 제3조 보호 정신)<br/>
              • AI 면책·오남용 방지 조항은 그대로 유지
            </p>
          </div>

          <p>③ 본 개정은 회원의 청약철회권(7일 이내 100% 환불)을 그대로 보장하며, 운영 절차를 명료화하기 위한 것입니다.</p>

          <div className="policy-card policy-card--warning">
            <p><strong>④ 시행 전 결제분에 대한 30일 유예 (회원 권익 보호 조치)</strong></p>
            <p>
              본 개정 시행일({SIMPLIFIED_POLICY_REVISION_DATE.replace(/-/g, '. ')}) 이전에 결제하셨고, 결제 후 7일이 경과한 회원이 본 개정 내용에 동의하지 않으시는 경우, 시행일로부터 30일 이내(2026. 5. 25.까지)에 <a href="mailto:angel@plactical.com">angel@plactical.com</a>으로 환불 요청 시 「전자상거래법」 제17조 제2항 제5호 및 직전 환불 정책(2025.12.18~2026.4.24 적용)에 따른 차감 환불(기간·사용량 기준 중 더 높은 비율로 차감 후 위약금 10% 공제)을 적용해 드립니다. 이는 「약관의 규제에 관한 법률」 제3조의 회원 보호 정신에 따른 자발적 조치입니다.
            </p>
          </div>

          <p>⑤ 이전의 환불 정책은 아래에서 확인하실 수 있습니다.</p>
          <ul>
            <li><a href="/help/notice/refund-policy-update-2025-12">2025. 12. 18 ~ 2026. 4. 24 적용 정책 (단가표·차감 환불)</a></li>
            <li><a href="/help/notice/refund-policy-previous-2024-12">2024. 12. 20 ~ 2025. 12. 17 적용 정책</a></li>
          </ul>
        </PolicySection>

        <PolicyRefundFooter />
      </main>
    </div>
  );
};

const PolicySection = ({ title, children }) => (
  <section className="policy-section">
    <h2>{title}</h2>
    {children}
  </section>
);

const PolicyRefundFooter = () => (
  <nav className="policy-related" aria-label="관련 정책">
    <div className="policy-related-links">
      <a className="policy-link" href="/company-info">회사정보</a>
      <a className="policy-link" href="/privacy-policy">개인정보처리방침</a>
      <a className="policy-link" href="/terms-of-service">이용약관</a>
    </div>
  </nav>
);

// ============================================================
// @deprecated 2026-04-25
// 본 컴포넌트는 NEW_POLICY_EFFECTIVE_DATE(2025-12-18) 이전 버전입니다.
// 현재 시스템 시각은 항상 시행일 이후이므로 실사용자에게 노출되지 않는 dead code이며,
// 약관 변경 이력 보존 목적으로만 유지됩니다 (변경 이력은 HelpCenterPage notices 에서도
// /help/notice/refund-policy-previous-2024-12 로 별도 보존).
// 향후 별도 정리 시점에 제거할 수 있습니다.
// ============================================================
// 현재 환불정책 (2025-12-18 이전)
// ============================================================
const CurrentRefundPolicy = () => {
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      padding: 'clamp(1rem, 4vw, 2rem)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: 'clamp(1.5rem, 5vw, 3rem)',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        lineHeight: 1.7
      }}>
        <h1 style={{
          fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
          fontWeight: '700',
          color: '#1a202c',
          marginBottom: '2rem',
          textAlign: 'center'
        }}>
          환불 정책
        </h1>

        {/* 변경 예정 안내 배너 */}
        <div style={{
          backgroundColor: '#fef3c7',
          padding: '1rem 1.5rem',
          borderRadius: '12px',
          marginBottom: '1.5rem',
          border: '1px solid #fde68a'
        }}>
          <p style={{
            fontSize: '14px',
            color: '#92400e',
            margin: 0,
            lineHeight: 1.6
          }}>
            <strong>환불 정책 변경 예정 안내</strong><br/>
            AI 서비스 비용 구조를 반영한 정책으로 2025년 12월 18일부터 변경됩니다. 
            <a href="/help" style={{ color: '#b45309', marginLeft: '4px' }}>자세히 보기</a>
          </p>
        </div>

        <div style={{
          backgroundColor: '#f0f4ff',
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          border: '1px solid #c7d2fe'
        }}>
          <p style={{
            fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
            color: '#3730a3',
            margin: 0,
            textAlign: 'center',
            fontWeight: '600'
          }}>
            시행일: 2024년 12월 20일 | 최종 개정일: 2025년 1월 16일<br/>
            전자상거래 등에서의 소비자보호에 관한 법률 준수
          </p>
        </div>

        <Section title="제1조 (환불 정책의 목적)">
          <p>이 환불 정책은 플랙티컬 주식회사가 제공하는 왓퀴즈 Pro 구독 서비스의 환불 기준과 절차를 명확히 합니다.</p>
        </Section>

        <Section title="제2조 (환불 기준)">
          <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#2d3748', marginBottom: '1rem' }}>
            7일 이내 청약철회권
          </h3>
          <div style={{
            backgroundColor: '#f0fff4',
            padding: '1.5rem',
            borderRadius: '12px',
            border: '1px solid #9ae6b4',
            marginBottom: '1.5rem'
          }}>
            <p style={{ margin: 0, fontWeight: '600', color: '#2f855a', marginBottom: '0.5rem' }}>
              100% 전액 환불
            </p>
            <ul style={{ paddingLeft: '1.5rem', margin: 0 }}>
              <li style={{ marginBottom: '0.5rem' }}>구독 시작일로부터 7일 이내</li>
              <li style={{ marginBottom: '0.5rem' }}>서비스 이용량과 무관하게 전액 환불</li>
            </ul>
          </div>

          <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#2d3748', marginBottom: '1rem' }}>
            7일 경과 후
          </h3>
          <div style={{
            backgroundColor: '#fffbeb',
            padding: '1.5rem',
            borderRadius: '12px',
            border: '1px solid #fcd34d'
          }}>
            <p style={{ margin: 0, fontWeight: '600', color: '#d97706', marginBottom: '0.5rem' }}>
              일할 계산 환불
            </p>
            <ul style={{ paddingLeft: '1.5rem', margin: 0 }}>
              <li style={{ marginBottom: '0.5rem' }}>사용하지 않은 기간에 대해 일할 계산 환불</li>
              <li style={{ marginBottom: '0.5rem' }}>계산식: (전체 구독료 ÷ 전체 구독일수) × 남은 일수</li>
            </ul>
          </div>
        </Section>

        <Section title="제3조 (환불 제외 사항)">
          <ul style={{ paddingLeft: '1.5rem' }}>
            <li style={{ marginBottom: '0.5rem' }}>이용약관 위반으로 인한 서비스 중단</li>
            <li style={{ marginBottom: '0.5rem' }}>허위 정보 제공으로 인한 계약 해지</li>
            <li style={{ marginBottom: '0.5rem' }}>반복적인 가입/환불 시도</li>
          </ul>
        </Section>

        <Section title="제4조 (환불 신청 방법)">
          <ul style={{ paddingLeft: '1.5rem' }}>
            <li style={{ marginBottom: '0.5rem' }}>마이페이지 → 구독 관리 → 환불 요청</li>
            <li style={{ marginBottom: '0.5rem' }}>이메일: angel@plactical.com</li>
            <li style={{ marginBottom: '0.5rem' }}>전화: 02-949-9158</li>
          </ul>
          <p style={{ marginTop: '1rem' }}>환불 처리: 신청일로부터 3-5 영업일 이내</p>
        </Section>

        <Section title="제5조 (소비자 분쟁 해결)">
          <ul style={{ paddingLeft: '1.5rem' }}>
            <li style={{ marginBottom: '0.5rem' }}>한국소비자원 소비자분쟁조정위원회: 1372</li>
            <li style={{ marginBottom: '0.5rem' }}>온라인 신청: www.ccn.go.kr</li>
          </ul>
        </Section>

        <RefundFooter />
      </div>
    </div>
  );
};

// 공통 컴포넌트
const Section = ({ title, children }) => (
  <section style={{ marginBottom: '2.5rem' }}>
    <h2 style={{
      fontSize: 'clamp(1.2rem, 3vw, 1.5rem)',
      fontWeight: '600',
      color: '#2d3748',
      marginBottom: '1rem',
      borderBottom: '2px solid #3182f6',
      paddingBottom: '0.5rem'
    }}>
      {title}
    </h2>
    <div style={{
      fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
      color: '#4a5568'
    }}>
      {children}
    </div>
  </section>
);

const RefundFooter = () => (
  <div style={{
    marginTop: '3rem',
    padding: '2rem 0',
    borderTop: '1px solid #e2e8f0',
    textAlign: 'center'
  }}>
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      gap: '2rem',
      flexWrap: 'wrap'
    }}>
      <a href="/company-info" style={{ color: '#3182f6', textDecoration: 'none', fontWeight: '500' }}>회사정보</a>
      <a href="/privacy-policy" style={{ color: '#3182f6', textDecoration: 'none', fontWeight: '500' }}>개인정보처리방침</a>
      <a href="/terms-of-service" style={{ color: '#3182f6', textDecoration: 'none', fontWeight: '500' }}>이용약관</a>
    </div>
  </div>
);

export default RefundPolicy;
